/*
  candle-builder.js
  ------------------------------------------------------------------
  Gestisce 3 scene Three.js (una per contenitore: glass, metal, ceramic).
  Per ognuna:
    - carica il modello .glb del CONTENITORE fornito dal merchant
      (impostato nelle sezioni theme, settings.*_model_url)
    - aggiunge via codice: CERA (dentro), STOPPINO (dentro), ETICHETTA (fuori)
    - aggiorna cera/stoppino/etichetta in tempo reale quando l'utente
      cambia le opzioni nel form.
  Se un URL modello non è ancora stato caricato, viene usato un
  cilindro placeholder al suo posto, cosi la pagina è testabile
  subito anche senza i modelli 3D definitivi.
------------------------------------------------------------------- */

import * as THREE from 'https://unpkg.com/three@0.160.0/build/three.module.js';
import { GLTFLoader } from 'https://unpkg.com/three@0.160.0/examples/jsm/loaders/GLTFLoader.js';
import { OrbitControls } from 'https://unpkg.com/three@0.160.0/examples/jsm/controls/OrbitControls.js';

const CONTAINER_TYPES = ['glass', 'metal', 'ceramic'];

// Colori base per tipo di cera (sovrascritti dal colore della fragranza scelta)
const WAX_BASE_COLORS = {
  soy: '#f7efe3',
  olive: '#e9dcb8',
};

const WICK_COLORS = {
  cotton: '#e8e2d0',
  wood: '#7a5330',
};

class CandleScene {
  constructor(canvas, containerType, modelUrl) {
    this.canvas = canvas;
    this.containerType = containerType;
    this.modelUrl = modelUrl;

    this.scene = new THREE.Scene();
    this.scene.background = null;

    const rect = canvas.getBoundingClientRect();
    this.width = rect.width || 220;
    this.height = rect.height || 220;

    this.camera = new THREE.PerspectiveCamera(35, this.width / this.height, 0.01, 100);
    this.camera.position.set(0, 0.12, 0.32);

    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(this.width, this.height);

    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.enablePan = false;
    this.controls.minDistance = 0.15;
    this.controls.maxDistance = 0.6;

    this._setupLights();
    this._setupPlaceholders();
    this._loadContainerModel();

    window.addEventListener('resize', () => this._handleResize());

    this._animate();
  }

  _setupLights() {
    const ambient = new THREE.AmbientLight(0xffffff, 0.7);
    this.scene.add(ambient);

    const key = new THREE.DirectionalLight(0xffffff, 0.9);
    key.position.set(0.5, 1, 0.6);
    this.scene.add(key);

    const fill = new THREE.DirectionalLight(0xffffff, 0.4);
    fill.position.set(-0.6, 0.3, -0.4);
    this.scene.add(fill);
  }

  /* Modello reale 10x10x10cm => 0.1 unità Three.js = 1 metro di riferimento */
  _setupPlaceholders() {
    // CERA: cilindro interno
    const waxGeometry = new THREE.CylinderGeometry(0.038, 0.038, 0.08, 48);
    this.waxMaterial = new THREE.MeshStandardMaterial({
      color: WAX_BASE_COLORS.soy,
      roughness: 0.6,
    });
    this.waxMesh = new THREE.Mesh(waxGeometry, this.waxMaterial);
    this.waxMesh.position.y = 0.0;
    this.scene.add(this.waxMesh);

    // STOPPINO: cilindro sottile al centro
    const wickGeometry = new THREE.CylinderGeometry(0.0015, 0.0015, 0.05, 12);
    this.wickMaterial = new THREE.MeshStandardMaterial({ color: WICK_COLORS.cotton });
    this.wickMesh = new THREE.Mesh(wickGeometry, this.wickMaterial);
    this.wickMesh.position.y = 0.06;
    this.scene.add(this.wickMesh);

    // CONTENITORE placeholder (sostituito appena il .glb è caricato, se presente)
    const containerGeometry = new THREE.CylinderGeometry(0.045, 0.045, 0.09, 48, 1, true);
    const containerMaterial = new THREE.MeshPhysicalMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.25,
      roughness: 0.1,
      transmission: this.containerType === 'glass' ? 0.9 : 0,
      metalness: this.containerType === 'metal' ? 0.8 : 0,
      side: THREE.DoubleSide,
    });
    this.containerPlaceholder = new THREE.Mesh(containerGeometry, containerMaterial);
    this.scene.add(this.containerPlaceholder);

    // ETICHETTA: piano curvo semplificato come piano piatto attorno al contenitore
    this.labelCanvas = document.createElement('canvas');
    this.labelCanvas.width = 512;
    this.labelCanvas.height = 128;
    this.labelTexture = new THREE.CanvasTexture(this.labelCanvas);
    const labelGeometry = new THREE.PlaneGeometry(0.07, 0.03);
    this.labelMaterial = new THREE.MeshBasicMaterial({
      map: this.labelTexture,
      transparent: true,
    });
    this.labelMesh = new THREE.Mesh(labelGeometry, this.labelMaterial);
    this.labelMesh.position.set(0, -0.005, 0.046);
    this.scene.add(this.labelMesh);

    this._drawLabelText('');
  }

  _loadContainerModel() {
    if (!this.modelUrl) return; // resta il placeholder

    const loader = new GLTFLoader();
    loader.load(
      this.modelUrl,
      (gltf) => {
        this.scene.remove(this.containerPlaceholder);
        this.containerModel = gltf.scene;
        // Normalizza scala assumendo un modello di 10x10x10cm circa
        const box = new THREE.Box3().setFromObject(this.containerModel);
        const size = new THREE.Vector3();
        box.getSize(size);
        const maxDim = Math.max(size.x, size.y, size.z) || 1;
        const targetSize = 0.1; // 10cm
        const scale = targetSize / maxDim;
        this.containerModel.scale.setScalar(scale);
        this.scene.add(this.containerModel);
      },
      undefined,
      (error) => {
        console.warn(`Impossibile caricare il modello per ${this.containerType}:`, error);
        // Il placeholder resta visibile
      }
    );
  }

  _drawLabelText(text) {
    const ctx = this.labelCanvas.getContext('2d');
    ctx.clearRect(0, 0, this.labelCanvas.width, this.labelCanvas.height);
    ctx.fillStyle = 'rgba(255,255,255,0.92)';
    ctx.fillRect(0, 0, this.labelCanvas.width, this.labelCanvas.height);
    ctx.strokeStyle = '#b8a98c';
    ctx.lineWidth = 4;
    ctx.strokeRect(4, 4, this.labelCanvas.width - 8, this.labelCanvas.height - 8);
    ctx.fillStyle = '#3a2f22';
    ctx.font = '600 48px serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, this.labelCanvas.width / 2, this.labelCanvas.height / 2);
    this.labelTexture.needsUpdate = true;
  }

  updateWax(waxType, tintColor) {
    const base = WAX_BASE_COLORS[waxType] || WAX_BASE_COLORS.soy;
    const color = new THREE.Color(tintColor || base);
    // mescola leggermente il colore fragranza con la base della cera
    const baseColor = new THREE.Color(base);
    color.lerp(baseColor, 0.35);
    this.waxMaterial.color.set(color);
  }

  updateWick(wickType) {
    this.wickMaterial.color.set(WICK_COLORS[wickType] || WICK_COLORS.cotton);
  }

  updateLabel(text) {
    this._drawLabelText(text);
  }

  setActive(isActive) {
    this.canvas.closest('.candle-viewer').dataset.active = isActive ? 'true' : 'false';
  }

  _handleResize() {
    const rect = this.canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    this.width = rect.width;
    this.height = rect.height;
    this.camera.aspect = this.width / this.height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(this.width, this.height);
  }

  _animate() {
    requestAnimationFrame(() => this._animate());
    this.controls.update();
    this.renderer.render(this.scene, this.camera);
  }
}

function initCandleBuilder(sectionId) {
  const root = document.getElementById(`candle-builder-${sectionId}`);
  if (!root) return;

  const form = root.querySelector('.candle-form');
  const scriptEl = document.querySelector(`script[data-section-id="${sectionId}"]`);

  // URL modelli letti da data-attributes iniettati dal Liquid (vedi sezione)
  const modelUrls = {
    glass: root.dataset.glassModel,
    metal: root.dataset.metalModel,
    ceramic: root.dataset.ceramicModel,
  };

  const scenes = {};
  CONTAINER_TYPES.forEach((type) => {
    const canvas = root.querySelector(`[data-canvas="${type}"]`);
    if (canvas) {
      scenes[type] = new CandleScene(canvas, type, modelUrls[type]);
    }
  });

  function setActiveContainer(type) {
    CONTAINER_TYPES.forEach((t) => scenes[t] && scenes[t].setActive(t === type));
  }

  function currentFragranceColor() {
    const select = form.querySelector('select[name="fragrance"]');
    const selected = select.options[select.selectedIndex];
    return selected ? selected.dataset.color : null;
  }

  function refreshAll() {
    const waxType = form.querySelector('input[name="wax"]:checked').value;
    const wickType = form.querySelector('input[name="wick"]:checked').value;
    const labelText = form.querySelector('input[name="label_text"]').value;
    const tint = currentFragranceColor();

    CONTAINER_TYPES.forEach((type) => {
      const scene = scenes[type];
      if (!scene) return;
      scene.updateWax(waxType, tint);
      scene.updateWick(wickType);
      scene.updateLabel(labelText);
    });
  }

  // Cambio contenitore
  form.querySelectorAll('input[name="container"]').forEach((input) => {
    input.addEventListener('change', (e) => setActiveContainer(e.target.value));
  });

  // Cambio fragranza / cera / stoppino
  form.querySelector('select[name="fragrance"]').addEventListener('change', refreshAll);
  form.querySelectorAll('input[name="wax"]').forEach((el) => el.addEventListener('change', refreshAll));
  form.querySelectorAll('input[name="wick"]').forEach((el) => el.addEventListener('change', refreshAll));

  // Etichetta personalizzata + contatore caratteri
  const labelInput = form.querySelector('input[name="label_text"]');
  const charCount = form.querySelector('[data-char-count]');
  labelInput.addEventListener('input', () => {
    charCount.textContent = labelInput.value.length;
    refreshAll();
  });

  // Stato iniziale
  setActiveContainer('glass');
  refreshAll();

  // Invio form: qui puoi collegare l'aggiunta al carrello con line item properties
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const formData = new FormData(form);
    const payload = {
      id: formData.get('id'),
      quantity: 1,
      properties: {
        Contenitore: formData.get('container'),
        Fragranza: formData.get('fragrance'),
        Cera: formData.get('wax'),
        Stoppino: formData.get('wick'),
        Etichetta: formData.get('label_text'),
      },
    };

    fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then((res) => res.json())
      .then(() => {
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      })
      .catch((err) => console.error('Errore aggiunta al carrello:', err));
  });
}

document.querySelectorAll('[id^="candle-builder-"]').forEach((root) => {
  const sectionId = root.id.replace('candle-builder-', '');
  initCandleBuilder(sectionId);
});
